<script setup>
import {computed, onMounted, onUnmounted, reactive, ref} from 'vue';
    import { useNotificationStore } from '@/stores/NotificationStore.js'

    const notificationStore = useNotificationStore()
    const props = defineProps({
        analysisUuid: {
            type: String,
            required: false,
            default: ''
        },
        redirectRoute: {
            type: String,
            required: false,
            default: 'expert.analyze.attach'
        },
        inviterUuid: {
            type: String,
            required: false,
            default: null
        },
        presetUserName: {
            type: String,
            required: false,
            default: '',
        },
        presetUserEmail: {
            type: String,
            required: false,
            default: '',
        },
        submitLabel: {
            type: String,
            required: false,
            default: 'Register for free'
        },
        webinarId: {
            type: [String, Number],
            required: false,
            default: null,
        }
    })

    const registration = reactive({
        user: {
            name: props.presetUserName,
            email: props.presetUserEmail,
            password: '',
        },
        company: {
            name: '',
            org_nr: '',
            country: '',
        },
        term_agreements: {
            data_processing: false,
            business_terms: false,
            user_agreement: false,
        },
    });

    const nameValidates = ref(false)
    const emailValidates = ref(false)
    const existingUser = ref(false)
    const passwordValidates = ref(false)

    const companyNameValidates = ref(false)
    const companyCountryValidates = computed(() => registration.company.country !== '')


    const step = ref('user');
    const working = ref(false)
    const emailValidationTimeout = ref(null)

    const userValidates = computed(() => {
        return nameValidates.value && emailValidates.value && passwordValidates.value
    })

    const companyValidates = computed(() => {
        return companyNameValidates.value
            && companyCountryValidates.value
            && registration.term_agreements.data_processing
            && registration.term_agreements.business_terms
    })

    const goBackToUser = () => {
        step.value = 'user'
    }

    const continueToCompany = () => {
        if (!userValidates.value) {
            return
        }

        step.value = 'interlude'
        setTimeout(() => {
            step.value = ''
        }, 2900)
        setTimeout(() => {
            step.value = 'company'
        }, 3500)
    }

    const submitRegistration = async () => {
        if (working.value || !companyValidates.value || !userValidates.value) {
            return
        }
        working.value = true

        const registrationData = {
            name: registration.user.name,
            email: registration.user.email,
            password: registration.user.password,
            company_name: registration.company.name,
            country: registration.company.country,
            redirectRoute: props.redirectRoute,
            redirectParams: {uuid: props.analysisUuid}
        }

        if (props.webinarId) {
            registrationData.webinar_id = props.webinarId
        }

        const res = await axios.post(route('expert.register.submit', props.inviterUuid), registrationData);

        if (res.data.success) {
            step.value = 'registered'

            setTimeout(function() {
                window.location.href = res.data.redirectUrl
            }, 2000)
        }
        if (res.data.error) {
            notificationStore.addNotification({
                type: 'warning',
                title: 'Email already registered',
                message: `There already is an account associated with ${registration.user.email}`,
                dismissAfter: 5000,
            })
        }
        working.value = false
    }

    const validateEmailDebounced = () => {
        if (emailValidationTimeout.value) {
            clearTimeout(emailValidationTimeout.value)
        }

        emailValidationTimeout.value = setTimeout(async () => {
            if (registration.user.email && emailValidates.value) {
                const res = await axios.post(route('expert.register.emailValidate'), {email: registration.user.email});
                existingUser.value = res.data.exists;
            }
        }, 500)
    }

    onMounted(() => {
        window.addEventListener('keyup', (event) => {
            if (event.key === 'Enter') {
                if (step.value === 'user') {
                    continueToCompany()
                }
                if (step.value === 'company') {
                    submitRegistration()
                }
            }
        })
    })

    onUnmounted(() => {
        if (emailValidationTimeout.value) {
            clearTimeout(emailValidationTimeout.value)
        }
    })

</script>

<template>
    <div class="relative max-w-xl mx-auto">
        <div
            class="relative z-10 text-gray-900 transition-all"
            :class="step === 'user' ? '' : '-translate-x-vw'"
        >
            <card-basic>

                <div class="relative space-x-2">
                    <input-text
                        class="w-full"
                        type="text"
                        label="Your name"
                        maxlength="255"
                        name="name"
                        @keypress="$event.key.match(/[a-zA-Z\s]/) ? true : $event.preventDefault()"
                        @validatesAs="$event === 'required' ? nameValidates = true : null"
                        @clearValidates="nameValidates = false"
                        required
                        v-model:content="registration.user.name"
                    />
                    <div class="absolute top-8 right-8">
                        <validation-mark
                            class="text-myflowGreen-600"
                            markClass="w-6 h-6"
                            v-if="nameValidates"
                        />
                    </div>
                </div>
                <div class="relative space-x-2">
                    <input-text
                        label="Email"
                        maxlength="255"
                        inputtype="email"
                        name="email"
                        @validatesAs="$event === 'email' ? emailValidates = true : null"
                        @clearValidates="emailValidates = false"
                        required
                        v-model:content="registration.user.email"
                        @keyup="validateEmailDebounced"
                    />
                    <div class="absolute top-8 right-8">
                        <validation-mark
                            class="text-myflowGreen-600"
                            markClass="w-6 h-6"
                            v-if="emailValidates"
                        />
                    </div>
                </div>

                <div v-if="existingUser">

                    <p>
                        Welcome back! It looks like you already have an account with us.
                    </p>

                    <a :href="route('login')">
                        <button-base
                            style-type="success"
                        >
                            To login, click here
                        </button-base>
                    </a>
                </div>

                <div v-if="!existingUser">
                    <div class="relative space-x-2">
                        <div>
                            <input-text
                                label="Password"
                                inputtype="password"
                                maxlength="255"
                                name="password"
                                @validatesAs="$event === 'password' ? passwordValidates = true : null"
                                @clearValidates="passwordValidates = false"
                                required
                                v-model:content="registration.user.password"
                            />
                            <p class="-mt-2 text-xs text-gray-700">Min 8 characters, one lowercase letter, one uppercase letter and one number</p>
                        </div>
                        <div class="absolute top-8 right-8">
                            <validation-mark
                                class="text-myflowGreen-600"
                                markClass="w-6 h-6"
                                v-if="passwordValidates"
                            />
                        </div>
                    </div>


                    <div class="relative mt-3 space-x-2">
                        <input-text
                            class="w-full"
                            type="text"
                            label="Business name"
                            maxlength="255"
                            name="name"
                            @validatesAs="$event === 'required' ? companyNameValidates = true : null"
                            @clearValidates="companyNameValidates = false"
                            required
                            v-model:content="registration.company.name"
                        />
                        <div class="absolute top-8 right-8">
                            <validation-mark
                                class="text-myflowGreen-600"
                                markClass="w-6 h-6"
                                v-if="companyNameValidates"
                            />
                        </div>
                    </div>
                    <div class="relative space-x-2">
                        <div>
                            <input-language
                                label="Country"
                                :required="true"
                                name="country"
                                v-model:content="registration.company.country"
                            ></input-language>
                        </div>
                        <div class="absolute top-12 right-8">
                            <validation-mark
                                class="text-myflowGreen-600"
                                markClass="w-6 h-6"
                                v-if="companyCountryValidates"
                            />
                        </div>
                    </div>

                    <div class="col-span-1">
                        <div>
                            <input
                                type="checkbox"
                                v-model="registration.term_agreements.data_processing"
                                name="agrees_to_data_processing"
                                required
                                value="yes"
                            >
                            <span>
                    I accept the
                    <a
                        :href="route('terms.pub')"
                        class="underline text-myflowPurple-900"
                        target="_blank"
                    >data processing agreement</a>
                </span>
                        </div>
                        <div>
                            <input
                                type="checkbox"
                                v-model="registration.term_agreements.business_terms"
                                name="agrees_to_business_terms"
                                required
                                value="yes"
                            >
                            <span>
                    I accept the
                    <a
                        :href="route('terms.company')"
                        class="underline text-myflowPurple-900"
                        target="_blank"
                    >business terms</a> &
                    <a
                        :href="route('terms.general')"
                        class="underline text-myflowPurple-900"
                        target="_blank"
                    >user agreement</a>
                </span>
                        </div>

                    </div>

                    <div class="flex justify-between mt-4">
                        <button-base
                            :style-type="userValidates && companyValidates ? 'success' : 'disabled'"
                            :disabled="!companyValidates || !userValidates || working"
                            @click="submitRegistration"
                        >
                            {{ props.submitLabel }}
                        </button-base>
                    </div>

                    <div class="flex justify-between mt-4 space-x-4">
                        <p class="text-xs">
                            No credit card required
                        </p>
                    </div>
            </div>

            </card-basic>
        </div>

        <div
            class="absolute inset-0 flex items-center justify-center p-8 transition-all duration-1000 delay-300"
            :class="step === 'interlude' ? 'opacity-100 scale-100' : 'opacity-0 scale-90'"
        >
            <div class="text-center">
                <p>Just a moment while we make sure everything is set up perfectly</p>
                <p class="italic strong">You look great by the way! ☀️</p>
            </div>
        </div>

        <div
            class="relative z-10 text-gray-900 transition-all"
            :class="step === 'company' ? '' : 'opacity-0 disabled-translate-x-vw'"
        >

        </div>

        <div
            class="absolute inset-0 z-0 flex items-center justify-center p-8 transition-all duration-1000 delay-300 pointer-events-none"
            :class="step === 'registered' ? 'opacity-100 scale-100' : 'opacity-0 scale-90'"
        >
            <div class="text-center">
                <h1>Sweet</h1>
                <p>Your account is set up and we're putting the final pieces in place</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.translate-x-vw {
    transform: translateX(100vw);
}
.-translate-x-vw {
    transform: translateX(-100vw);
}
</style>
